import {
    all,
    take,
    takeEvery,
    takeLatest,
    select,
    apply,
    put,
    call,
} from 'redux-saga/effects';

import {
    FETCH_ACCOUNT_SUCCEEDED,

    ATTEMPT_FETCH_PROGRAMS,
    FETCH_PROGRAMS_SUCCEEDED,

    ATTEMPT_FETCH_PROGRAM,
    FETCH_PROGRAM_SUCCEEDED,

    ATTEMPT_CREATE_PROGRAM,
    CREATE_PROGRAM_SUCCEEDED,

    ATTEMPT_DUPLICATE_PROGRAM,
    DUPLICATE_PROGRAM_SUCCEEDED,

    ATTEMPT_UPDATE_PROGRAM,
    UPDATE_PROGRAM_SUCCEEDED,

    ATTEMPT_DELETE_PROGRAM,
    DELETE_PROGRAM_SUCCEEDED,
    DELETE_PROGRAM_FAILED,

    ATTEMPT_UPDATE_PROGRAM_SHARING,
    UPDATE_PROGRAM_SHARING_SUCCEEDED,
    UPDATE_PROGRAM_SHARING_FAILED,

    ATTEMPT_UPDATE_PROGRAM_CONTACT_EMAIL,
    UPDATE_PROGRAM_CONTACT_EMAIL_SUCCEEDED,
    UPDATE_PROGRAM_CONTACT_EMAIL_FAILED,

    UPDATE_PROGRAMS_TAB,

    QUEUE_ITEM_SUCCEEDED,
    QUEUE_ITEM_FAILED,
} from 'shared_redux/actions';
import API from 'services/programs';
import { handleError } from 'services/alerts';
import * as AccountsSelectors from 'shared_redux/selectors/models/accounts';
import * as ProgramsSelectors from 'shared_redux/selectors/view/programs';
import { getEffectiveOrganizationId } from 'shared_redux/selectors/view/organization_switcher';

export default function* ProgramsSaga() {
    yield all([
        takeLatest(ATTEMPT_FETCH_PROGRAMS, fetchPrograms),
        takeLatest(UPDATE_PROGRAMS_TAB, handleTabChange),
        takeLatest(ATTEMPT_FETCH_PROGRAM, fetchProgram),
        takeEvery(ATTEMPT_CREATE_PROGRAM, createProgram),
        takeEvery(ATTEMPT_DUPLICATE_PROGRAM, duplicateProgram),
        takeEvery(ATTEMPT_UPDATE_PROGRAM, updateProgram),
        takeEvery(ATTEMPT_DELETE_PROGRAM, deleteProgram),
        takeEvery(ATTEMPT_UPDATE_PROGRAM_SHARING, updateProgramSharing),
        takeEvery(ATTEMPT_UPDATE_PROGRAM_CONTACT_EMAIL, updateProgramContactEmail),
    ]);
};

function *fetchPrograms(action) {
    try {
        // Use the effective organization ID from the organization switcher state
        // This ensures we use the current selected organization, not the stale account data
        let organizationId = yield select(getEffectiveOrganizationId);

        if (!organizationId) {
            // If no organization ID is available, wait for account to be fetched
            let account = yield select(AccountsSelectors.getAccount);
            if (!account) {
                yield take(FETCH_ACCOUNT_SUCCEEDED);
                account = yield select(AccountsSelectors.getAccount);
            }
            organizationId = account.organization_id;
        }

        // Get current tab to filter programs
        const currentTab = yield select(ProgramsSelectors.getCurrentTab);
        const visibility = currentTab === 'all' ? undefined : currentTab;

        const programs = yield apply(API, API.getPrograms, [organizationId, visibility]);
        yield put({
            type: FETCH_PROGRAMS_SUCCEEDED,
            payload: programs,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to fetch programs');
    }
}

function *handleTabChange(action) {
    // When tab changes, refetch programs with new visibility
    yield put({ type: ATTEMPT_FETCH_PROGRAMS });
}

function *fetchProgram(action) {
    try {
        // Use the effective organization ID from the organization switcher state
        let organizationId = yield select(getEffectiveOrganizationId);
        
        if (!organizationId) {
            // If no organization ID is available, wait for account to be fetched
            let account = yield select(AccountsSelectors.getAccount);
            if (!account) {
                yield take(FETCH_ACCOUNT_SUCCEEDED);
                account = yield select(AccountsSelectors.getAccount);
            }
            organizationId = account.organization_id;
        }
        
        const program = yield apply(API, API.getProgram, [action.program_id, organizationId]);
        yield put({
            type: FETCH_PROGRAM_SUCCEEDED,
            payload: program,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to fetch program');
    }
}

function *createProgram(action) {
    try {
        let organizationId = yield select(getEffectiveOrganizationId);
        if (!organizationId) {
            let account = yield select(AccountsSelectors.getAccount);
            if (!account) {
                yield take(FETCH_ACCOUNT_SUCCEEDED);
                account = yield select(AccountsSelectors.getAccount);
            }
            organizationId = account.organization_id;
        }
        const payload = {
            ...action.payload,
            organization_id: organizationId,
        };
        const result = yield apply(API, API.createProgram, [payload]);
        yield put({ type: QUEUE_ITEM_SUCCEEDED });
        yield put({
            type: CREATE_PROGRAM_SUCCEEDED,
            payload: result,
        });
    } catch(error) {
        if (error.statusCode === 409) {
            // already created, treat as success
            yield put({ type: QUEUE_ITEM_SUCCEEDED });
        } else {
            yield put({ type: QUEUE_ITEM_FAILED });
            yield handleError(error, 'Error: Unable to create program');
        }
    }
}

// TODO: consider making this not part of the queue
function *duplicateProgram(action) {
    try {
        const result = yield apply(API, API.createProgram, [{source_program_id: action.id}]);
        yield put({ type: QUEUE_ITEM_SUCCEEDED });
        yield put({
            type: DUPLICATE_PROGRAM_SUCCEEDED,
            payload: result,
        });
    } catch(error) {
        yield put({ type: QUEUE_ITEM_FAILED });
        yield handleError(error, 'Error: Unable to duplicate program');
    }
}

function *updateProgram(action) {
    try {
        const result = yield apply(API, API.updateProgram, [action.payload]);
        yield put({ type: QUEUE_ITEM_SUCCEEDED });
        yield put({
            type: UPDATE_PROGRAM_SUCCEEDED,
            payload: result,
        });
    } catch(error) {
        yield put({ type: QUEUE_ITEM_FAILED });
        yield handleError(error, 'Error: Unable to update program');
    }
}

// TODO: spinner for delete program rather than instant
function *deleteProgram(action) {
    try {
        yield apply(API, API.deleteProgram, [action.id]);
        yield put({ type: QUEUE_ITEM_SUCCEEDED });
        yield put({
            type: DELETE_PROGRAM_SUCCEEDED,
            id: action.id,
        });
    } catch(error) {
        if (error.statusCode === 403) {
            // POSSIBLY already deleted, going to assume it and treat it as success
            yield put({
                type: DELETE_PROGRAM_SUCCEEDED,
                id: action.id,
            });
        } else {
            yield put({
                type: DELETE_PROGRAM_FAILED,
                id: action.id,
            });
            yield handleError(error, 'Error: Unable to delete program');
        }
    }
}

function *updateProgramSharing(action) {
    try {
        const result = yield apply(API, API.updateProgramSharing, [action.program_id, action.is_public]);
        yield put({ type: QUEUE_ITEM_SUCCEEDED });
        yield put({
            type: UPDATE_PROGRAM_SHARING_SUCCEEDED,
            payload: result,
        });
    } catch(error) {
        yield put({
            type: UPDATE_PROGRAM_SHARING_FAILED,
            error,
        });
        yield put({ type: QUEUE_ITEM_FAILED });
        yield handleError(error, 'Error: Unable to update program sharing');
    }
}

function *updateProgramContactEmail(action) {
    try {
        const result = yield apply(API, API.updateProgramContactEmail, [action.program_id, action.contact_email]);
        yield put({ type: QUEUE_ITEM_SUCCEEDED });
        yield put({
            type: UPDATE_PROGRAM_CONTACT_EMAIL_SUCCEEDED,
            payload: result,
        });
    } catch(error) {
        yield put({
            type: UPDATE_PROGRAM_CONTACT_EMAIL_FAILED,
            error,
        });
        yield put({ type: QUEUE_ITEM_FAILED });
        yield handleError(error, 'Error: Unable to update program contact email');
    }
}
